/**
 * One numbered step card (the approved editor pattern): collapsed = one line
 * with a truthful summary; open = body, summary hidden. headerRight
 * (switches etc.) sits beside the toggle button, never inside it — so it
 * never toggles the card and the markup has no button inside a button.
 * `onLaunch` turns the card into a door: clicking it opens a pop-up (the
 * Automation's optimizers) instead of expanding — an expand icon replaces
 * the chevron and there is no body.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { Check, ChevronDown, LucideIcon, Maximize2 } from 'lucide-react';

import { cn } from '@/lib/utils';

import { useStepGroup } from './StepCardGroup';
import { stepTone, type StepTone } from './step-tone';
import { useOptionalStepTone } from './step-tone-context';

interface Props {
  /** 1-based position; also the number in the square. */
  index: number;
  icon: LucideIcon;
  title: string;
  /** One-line collapsed summary — chips/mono welcome, keep it truthful. */
  summary: ReactNode;
  /** Slot right of the chevron zone (e.g. an enable Switch). Never toggles. */
  headerRight?: ReactNode;
  /** Visited/completed — a check replaces the number when the card is closed. */
  done?: boolean;
  /** Opens a pop-up instead of expanding (no body). */
  onLaunch?: () => void;
  /** Accent. Falls back to the tab's StepToneProvider, then primary. */
  tone?: StepTone;
  children?: ReactNode;
}

export const StepCard: FC<Props> = ({
  index,
  icon: Icon,
  title,
  summary,
  headerRight,
  done = false,
  onLaunch,
  tone,
  children,
}) => {
  const { openIndex, toggle } = useStepGroup();
  const inherited = useOptionalStepTone();
  const accent = tone ?? inherited;
  const paint = stepTone(accent);
  const open = !onLaunch && openIndex === index;
  const press = () => (onLaunch ? onLaunch() : toggle(index));

  return (
    <div
      className={cn(
        'rounded-xl border transition-colors',
        accent && 'bg-background',
        open ? paint.cardOpen : paint.cardIdle,
        onLaunch && paint.launch,
      )}
    >
      <div className="flex items-center gap-3 pr-5">
        <button
          type="button"
          aria-expanded={onLaunch ? undefined : open}
          aria-haspopup={onLaunch ? 'dialog' : undefined}
          onClick={press}
          className="flex w-full min-w-0 flex-1 items-center gap-3 py-3.5 pl-5 text-left"
        >
          <span
            className={cn(
              'flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md font-mono text-xs font-semibold',
              open ? paint.badgeOpen : done ? paint.badgeDone : paint.badgeIdle,
            )}
            aria-label={done && !open ? `step ${index}, done` : undefined}
          >
            {done && !open ? <Check className="h-3.5 w-3.5" /> : index}
          </span>
          <span className="flex shrink-0 items-center gap-2 text-[13.5px] font-semibold">
            <Icon className={cn('h-3.5 w-3.5', paint.icon)} /> {title}
          </span>
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-xs text-muted-foreground',
              open && 'invisible',
            )}
          >
            {summary}
          </span>
        </button>
        {headerRight ? (
          <span className="flex shrink-0 items-center">{headerRight}</span>
        ) : null}
        <button
          type="button"
          tabIndex={-1}
          aria-hidden
          onClick={press}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted"
        >
          {onLaunch ? (
            <Maximize2 className="h-4 w-4" />
          ) : (
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', open && 'rotate-180')}
            />
          )}
        </button>
      </div>
      {open && children ? (
        <div className={cn('border-t px-5 pb-5 pt-4', paint.body)}>
          {children}
        </div>
      ) : null}
    </div>
  );
};
